import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wifi, WifiOff, CloudUpload, CheckCircle2, AlertTriangle, Download, Tablet, Search, UserPlus, Trash2, Pencil } from "lucide-react";
import { api, errMsg, fmtTime, fmtDate, fmtClock } from "../lib/api";
import { useAuth } from "../lib/auth";
import { vaultGet, vaultSet, requestPersist, newId } from "../lib/offlineStore";
import { ParishMark, inputCls, StatusBadge } from "../components/common";
import { ParticipantFields, RepresentFields, emptyParticipant, validParticipant } from "../components/ParticipantForm";

const ACK = ["accepted", "duplicate", "conflict", "already_received"];

function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const a = () => setOn(true), b = () => setOn(false);
    window.addEventListener("online", a);
    window.addEventListener("offline", b);
    return () => { window.removeEventListener("online", a); window.removeEventListener("offline", b); };
  }, []);
  return on;
}

function SyncBanner({ pending, online, lastSync, syncing }) {
  if (pending > 0)
    return (
      <div className="rounded-2xl bg-amber-100 border-2 border-amber-400 text-amber-900 p-4 flex items-center gap-3" data-testid="banner-offline-pending">
        <WifiOff className="h-6 w-6 shrink-0" />
        <div><div className="font-bold tracking-wide">SAVED OFFLINE — AWAITING INTERNET CONNECTION</div>
          <div className="text-sm">{pending} record(s) stored on this device only. They are PROVISIONAL and not yet part of the official parish attendance. {online ? (syncing ? "Uploading now…" : "Will retry automatically.") : "Will upload automatically when internet returns."}</div></div>
      </div>
    );
  if (lastSync)
    return (
      <div className="rounded-2xl bg-green-100 border-2 border-green-500 text-green-900 p-4 flex items-center gap-3" data-testid="banner-all-synced">
        <CheckCircle2 className="h-6 w-6 shrink-0" />
        <div><div className="font-bold tracking-wide">ALL ATTENDANCE RECORDS SUCCESSFULLY SYNCHRONIZED</div><div className="text-sm">Last confirmed by the parish server {fmtTime(lastSync)}</div></div>
      </div>
    );
  return null;
}

export default function Station() {
  const { user } = useAuth();
  const online = useOnline();
  const [device, setDevice] = useState(undefined);
  const [pkgs, setPkgs] = useState({});
  const [queue, setQueue] = useState([]);
  const [synced, setSynced] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [mid, setMid] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const qRef = useRef([]);
  qRef.current = queue;

  useEffect(() => {
    (async () => {
      setDevice(await vaultGet("device"));
      setPkgs(await vaultGet("pkgs", {}));
      setQueue(await vaultGet("queue", []));
      setSynced(await vaultGet("synced", []));
      setLastSync(await vaultGet("lastSync"));
      requestPersist();
    })();
  }, []);

  const saveQueue = async (q) => { setQueue(q); await vaultSet("queue", q); };

  const sync = useCallback(async () => {
    const dev = await vaultGet("device");
    if (!navigator.onLine || !dev || !localStorage.getItem("sfap_staff_token")) return;
    const q = await vaultGet("queue", []);
    const pending_by_meeting = {};
    q.forEach((r) => { pending_by_meeting[r.meeting_id] = (pending_by_meeting[r.meeting_id] || 0) + 1; });
    setSyncing(true);
    try {
      const recs = q.map(({ display_name, sync_status, sync_detail, ...r }) => r);
      const res = await api.post("/offline/sync", { records: recs, pending_by_meeting }, { headers: { "X-Device-Id": dev.device_id, "X-Device-Secret": dev.device_secret } });
      const byId = Object.fromEntries(res.data.results.map((x) => [x.offline_record_id, x]));
      const done = q.filter((r) => ACK.includes(byId[r.offline_record_id]?.result));
      const latest = await vaultGet("queue", []);
      const keep = latest.filter((r) => !done.some((d) => d.offline_record_id === r.offline_record_id))
        .map((r) => (byId[r.offline_record_id] ? { ...r, sync_status: byId[r.offline_record_id].result, sync_detail: byId[r.offline_record_id].detail } : r));
      await saveQueue(keep);
      const log = [...done.map((r) => ({ ...r, result: byId[r.offline_record_id].result, synced_at: new Date().toISOString() })), ...(await vaultGet("synced", []))].slice(0, 300);
      setSynced(log);
      await vaultSet("synced", log);
      if (done.length && !keep.length) {
        const now = new Date().toISOString();
        setLastSync(now);
        await vaultSet("lastSync", now);
        toast.success(`${done.length} record(s) synchronized`);
      }
      if (keep.length === 0 && q.length === 0) {
        await api.post("/offline/sync", { records: [], pending_by_meeting: {} }, { headers: { "X-Device-Id": dev.device_id, "X-Device-Secret": dev.device_secret } }).catch(() => null);
      }
    } catch (e) {
      if (e.response) toast.error(`Sync failed: ${errMsg(e)}. Records kept safely on this device.`);
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    if (online) sync();
    const t = setInterval(() => { if (navigator.onLine) sync(); }, 20000);
    const vis = () => { if (document.visibilityState === "visible" && navigator.onLine) sync(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [online, sync]);

  useEffect(() => {
    const warn = (e) => { if (qRef.current.length) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  if (device === undefined) return null;
  const pkg = mid ? pkgs[mid] : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-4 border-b border-[#E5DEC9] bg-[#F7F4EE]">
        <ParishMark small />
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold ${online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`} data-testid="station-connection-status">
            {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}{online ? "ONLINE" : "NO INTERNET"}
          </span>
          {device && <span className="text-sm text-stone-600 flex items-center gap-1" data-testid="station-device-name"><Tablet className="h-4 w-4" />{device.name} ({device.code})</span>}
          {user && user.role !== "monitor" && <Link to="/admin" className="text-sm underline">Admin</Link>}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 grid gap-5">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#2D3B2D]">Offline Registration Station</h1>
        <SyncBanner pending={queue.length} online={online} lastSync={lastSync} syncing={syncing} />
        <div className="rounded-xl bg-[#F5F0E6] border border-[#E5DEC9] p-3 text-sm text-stone-700 flex gap-2" data-testid="station-storage-warning">
          <AlertTriangle className="h-5 w-5 text-[#C85A32] shrink-0" />
          Do not clear browser data, use private/incognito mode, or uninstall this app while records are awaiting synchronization. Records are encrypted on this device.
        </div>
        {!device || !Object.keys(pkgs).length || !mid ? (
          <Setup user={user} online={online} device={device} setDevice={setDevice} pkgs={pkgs} setPkgs={setPkgs} setMid={setMid} />
        ) : (
          <Operate pkg={pkg} device={device} queue={queue} saveQueue={saveQueue} synced={synced} back={() => setMid(null)} sync={sync} online={online} syncing={syncing} />
        )}
      </main>
    </div>
  );
}

function Setup({ user, online, device, setDevice, pkgs, setPkgs, setMid }) {
  const [devices, setDevices] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const signed = user && user.role !== "monitor" && localStorage.getItem("sfap_staff_token");
  useEffect(() => {
    if (!online || !signed) return;
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => {});
    api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {});
  }, [online, signed]);
  const bind = async (d) => {
    try {
      const r = await api.post(`/devices/${d.id}/bind`);
      await vaultSet("device", r.data);
      setDevice(r.data);
      toast.success("This device is now prepared as an approved registration device");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fetchPkg = async (m) => {
    try {
      const r = await api.get(`/offline/package/${m.id}`, { headers: { "X-Device-Id": device.device_id, "X-Device-Secret": device.device_secret } });
      const next = { ...pkgs, [m.id]: r.data };
      await vaultSet("pkgs", next);
      setPkgs(next);
      toast.success(`Downloaded "${m.title}" with ${r.data.participants.length} cached profiles. Test by turning off Wi-Fi.`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cached = Object.values(pkgs);
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {cached.length > 0 && (
        <div className="sfap-card p-5 lg:col-span-2">
          <h2 className="font-serif font-bold text-lg mb-3">Meetings available offline on this device</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {cached.map((p) => (
              <button key={p.meeting.id} onClick={() => setMid(p.meeting.id)} className="text-left rounded-xl border border-[#E5DEC9] p-4 hover:border-[#2D3B2D]" data-testid={`station-open-${p.meeting.code}`}>
                <div className="font-semibold">{p.meeting.title}</div>
                <div className="text-sm text-stone-600">{fmtDate(p.meeting.date)} · window {fmtTime(p.authorization.window_start)} – {fmtTime(p.authorization.window_end)}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {!signed ? (
        <div className="sfap-card p-5">
          <h2 className="font-serif font-bold text-lg">Step 1 · Sign in while internet is available</h2>
          <p className="text-sm text-stone-600 mt-1">An authorized attendance officer or the Administrator must sign in to prepare this device.</p>
          {online ? <Link to="/login" className="btn-big btn-primary mt-4" data-testid="station-login-link">Sign in</Link> : <p className="mt-3 text-red-700">Internet is required for preparation.</p>}
        </div>
      ) : (
        <>
          <div className="sfap-card p-5">
            <h2 className="font-serif font-bold text-lg">Step 2 · Prepare this device</h2>
            <p className="text-sm text-stone-600 mt-1">Choose the registration device the Administrator registered for you.</p>
            <div className="grid gap-2 mt-3">
              {devices.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-xl border border-[#E5DEC9] p-3">
                  <span>{d.name} <span className="text-xs text-stone-500">{d.code}</span></span>
                  {device?.device_id === d.id ? <span className="text-green-700 text-sm font-semibold">This device</span> : <button className="btn-sm btn-ghost" onClick={() => bind(d)} data-testid={`bind-device-${d.code}`}>Use this device</button>}
                </div>
              ))}
              {!devices.length && <p className="text-sm text-stone-500">No device assigned. Ask the Administrator to register one under Offline Devices.</p>}
            </div>
          </div>
          <div className="sfap-card p-5">
            <h2 className="font-serif font-bold text-lg">Step 3 · Download authorized meeting</h2>
            <p className="text-sm text-stone-600 mt-1">Only meetings where the Administrator authorized this device for offline registration can be downloaded.</p>
            <div className="grid gap-2 mt-3">
              {meetings.filter((m) => device && m.offline_auth?.enabled && m.offline_auth.device_ids?.includes(device.device_id)).map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 rounded-xl border border-[#E5DEC9] p-3">
                  <span className="text-sm"><b>{m.title}</b><br />{fmtDate(m.date)} {fmtClock(m.start_time)}</span>
                  <button className="btn-sm btn-primary" onClick={() => fetchPkg(m)} data-testid={`download-pkg-${m.code}`}><Download className="h-4 w-4" />{pkgs[m.id] ? "Refresh" : "Download"}</button>
                </div>
              ))}
              {!device && <p className="text-sm text-stone-500">Prepare the device first.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Operate({ pkg, device, queue, saveQueue, synced, back, sync, online, syncing }) {
  const m = pkg.meeting;
  const auth = pkg.authorization;
  const dir = pkg.directory;
  const [sessionId, setSessionId] = useState(m.sessions[0].id);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);
  const inWindow = now >= new Date(auth.window_start).getTime() && now <= new Date(auth.window_end).getTime();
  const mine = queue.filter((r) => r.meeting_id === m.id);
  const gName = (id) => { const g = dir.gkks.find((x) => x.id === id); return g ? `${g.name} – ${g.locality}` : ""; };
  const taken = useMemo(() => new Set([...mine.filter((r) => r.session_id === sessionId).map((r) => r.participant_id),
    ...pkg.already_recorded.filter((r) => r.session_id === sessionId && r.status !== "pre_registered").map((r) => r.participant_id)]), [mine, sessionId, pkg]);
  const results = q.length >= 2 ? pkg.participants.filter((p) => `${p.display_name} ${p.code}`.toLowerCase().includes(q.toLowerCase())).slice(0, 12) : [];

  const add = async (rec) => {
    if (!inWindow) return toast.error("Outside the offline registration window authorized by the Administrator");
    const base = { offline_record_id: newId(), meeting_id: m.id, session_id: sessionId, local_checkin_at: new Date().toISOString(), device_id: device.device_id,
      verification_method: "officer_offline", auth_token: auth.token, sync_status: "pending" };
    await saveQueue([...queue, { ...base, ...rec }]);
    toast.success("Saved offline on this device");
    if (navigator.onLine) setTimeout(sync, 300);
  };
  const checkExisting = (p) => {
    if (taken.has(p.id)) return toast.warning(`${p.display_name} is already recorded for this session (possible duplicate)`);
    add({ participant_id: p.id, represent: { ministry_id: p.affiliations?.[0]?.ministry_id, position: p.affiliations?.[0]?.position }, display_name: p.display_name });
  };
  const saveNew = async () => {
    const v = form.v;
    const name = v.type === "external" ? v.full_name : `${v.first_name} ${v.last_name}`;
    const key = name.toLowerCase().replace(/[^a-z]/g, "");
    const dup = pkg.participants.find((p) => (p.display_name || "").toLowerCase().replace(/[^a-z]/g, "").includes(key));
    if (dup && !form.dupOk) { setForm({ ...form, dupOk: true }); return toast.warning(`Possible duplicate: ${dup.display_name} (${dup.code}). Search and check in instead, or press Save again to record as new.`); }
    if (form.editId) {
      await saveQueue(queue.map((r) => (r.offline_record_id === form.editId ? { ...r, new_participant: r.participant_id ? undefined : v, represent: form.rep, display_name: r.participant_id ? r.display_name : name } : r)));
      toast.success("Pending entry corrected");
    } else await add({ new_participant: v, represent: form.rep, display_name: name });
    setForm(null);
  };
  const remove = async (r) => { if (window.confirm(`Remove pending entry for ${r.display_name}? It has not been uploaded.`)) await saveQueue(queue.filter((x) => x.offline_record_id !== r.offline_record_id)); };

  return (
    <div className="grid gap-5">
      <div className="sfap-card p-5 flex flex-wrap justify-between gap-3 items-start">
        <div>
          <button onClick={back} className="text-sm text-stone-500">← Cached meetings</button>
          <h2 className="font-serif text-xl font-bold mt-1" data-testid="station-meeting-title">{m.title}</h2>
          <p className="text-sm text-stone-600">{fmtDate(m.date)} · {m.venue} · Authorized by {auth.authorized_by}</p>
          <p className={`text-sm font-semibold mt-1 ${inWindow ? "text-green-700" : "text-red-700"}`} data-testid="station-window-status">
            Offline window {fmtTime(auth.window_start)} – {fmtTime(auth.window_end)} · {inWindow ? "AUTHORIZED NOW" : "NOT WITHIN AUTHORIZED WINDOW — registration blocked"}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-stone-500">Saved on this device</div>
          <div className="font-serif text-4xl font-black text-[#5C3A21]" data-testid="station-device-count">{mine.length}</div>
          <div className="text-xs text-stone-500">provisional · {synced.filter((s) => s.meeting_id === m.id).length} already synchronized</div>
          <button className="btn-sm btn-ghost mt-2" disabled={!online || syncing || !queue.length} onClick={sync} data-testid="station-sync-now"><CloudUpload className="h-4 w-4" />Sync now</button>
        </div>
      </div>
      {m.sessions.length > 1 && (
        <select className={inputCls} value={sessionId} onChange={(e) => setSessionId(e.target.value)} data-testid="station-session">{m.sessions.map((s) => <option key={s.id} value={s.id}>{s.label} · {s.date}</option>)}</select>
      )}
      {!form ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="sfap-card p-5">
            <h3 className="font-serif font-bold mb-3 flex items-center gap-2"><Search className="h-4 w-4" />Returning participant (cached profiles)</h3>
            <input className={`${inputCls} h-14 text-lg`} placeholder="Type name or Participant ID" value={q} onChange={(e) => setQ(e.target.value)} data-testid="station-search" disabled={!inWindow} />
            <div className="mt-3 grid gap-2">
              {results.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 rounded-xl border border-[#E5DEC9] p-3">
                  <div><div className="font-semibold">{p.display_name}</div><div className="text-xs text-stone-500">{p.code} · {p.type === "parish" ? gName(p.gkk_id) : p.organization}</div></div>
                  <button className="btn-sm btn-primary" disabled={!inWindow} onClick={() => checkExisting(p)} data-testid={`station-checkin-${p.code}`}>{taken.has(p.id) ? "Already recorded" : "Check in"}</button>
                </div>
              ))}
              {q.length >= 2 && !results.length && <p className="text-sm text-stone-500">No cached match. Register as new participant.</p>}
            </div>
          </div>
          <div className="sfap-card p-5 grid gap-3 content-start">
            <h3 className="font-serif font-bold flex items-center gap-2"><UserPlus className="h-4 w-4" />New participant</h3>
            <button className="btn-big btn-ghost" disabled={!inWindow} onClick={() => setForm({ v: emptyParticipant("parish"), rep: {} })} data-testid="station-new-parish">Parishioner (SFAP)</button>
            <button className="btn-big btn-ghost" disabled={!inWindow} onClick={() => setForm({ v: emptyParticipant("external"), rep: { role_in_meeting: "Ordinary Participant" } })} data-testid="station-new-guest">Guest / outside the parish</button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {(!form.editId || form.v) && <ParticipantFields value={form.v} onChange={(v) => setForm({ ...form, v, dupOk: false })} dir={dir} prefix="st" />}
          <RepresentFields value={form.rep} onChange={(rep) => setForm({ ...form, rep })} dir={dir} type={form.v.type} prefix="st-rep" />
          <div className="flex gap-3">
            <button className="btn-big btn-primary" disabled={!validParticipant(form.v)} onClick={saveNew} data-testid="station-save-offline">Save offline</button>
            <button className="btn-big btn-ghost" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div className="sfap-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E5DEC9] font-serif font-bold">Pending on this device ({mine.length}) — PROVISIONAL OFFLINE ATTENDANCE</div>
        <div className="divide-y divide-[#F0EADC]" data-testid="station-pending-list">
          {mine.map((r) => (
            <div key={r.offline_record_id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-2">
              <div><div className="font-semibold">{r.display_name}</div><div className="text-xs text-stone-500">local check-in {fmtTime(r.local_checkin_at)} · {r.new_participant ? "new profile" : "existing profile"} · ID {r.offline_record_id.slice(0, 8)}</div></div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-amber-800">{r.sync_status === "pending" ? "SAVED OFFLINE" : (r.sync_status || "").toUpperCase()}</span>
                <button className="btn-sm btn-ghost" onClick={() => setForm({ v: r.new_participant || { type: "parish" }, rep: r.represent || {}, editId: r.offline_record_id })} disabled={!r.new_participant} title="Correct"><Pencil className="h-4 w-4" /></button>
                <button className="btn-sm btn-ghost" onClick={() => remove(r)} title="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {!mine.length && <p className="px-5 py-6 text-sm text-stone-500">No pending records.</p>}
        </div>
      </div>
      {synced.filter((s) => s.meeting_id === m.id).length > 0 && (
        <div className="sfap-card p-5">
          <h3 className="font-serif font-bold mb-2">Synchronized from this device</h3>
          {synced.filter((s) => s.meeting_id === m.id).slice(0, 20).map((s) => (
            <div key={s.offline_record_id} className="flex justify-between text-sm py-1 border-b border-dashed border-[#EFE8D8]"><span>{s.display_name}</span><StatusBadge status={s.result === "accepted" ? "present" : "disputed"} testid={`synced-${s.offline_record_id}`} /></div>
          ))}
          <p className="text-xs text-stone-500 mt-2">“Disputed” here means the server flagged a duplicate or conflict for Administrator review.</p>
        </div>
      )}
    </div>
  );
}
